import React, { useEffect, useState } from "react";
import { Col, Container, Row, Card, Form, Button, Spinner } from "react-bootstrap";
import StatCard from "../../Components/DashboardCards/StatCard/StatCard";
import useApi from "../../Hooks/useApi";
import * as api from "../../api/getapi";
import ReactSelect from "../../Components/ReactSelect";
import moment from "moment/moment";
import { BiRefresh, BiCart, BiCheckCircle, BiTime, BiXCircle, BiDollar, BiPackage } from "react-icons/bi";
import { formatNumber } from "../../Utilities/numbers";
import { ECHART_COLORS } from "../../Utilities/echartsTheme";

// Chart JS
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import DatePicker from "../../Components/DatePicker/DatePicker";
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const OrdersStats = () => {
  const fetchOrdersStats = useApi(api.fetchDeliverForYouOrdersStats, { hasCatchError: true });
  const fetchOrdersTrend = useApi(api.fetchDeliverForYouOrdersTrend, { hasCatchError: true });
  const [shop, setShop] = useState({
    value: null,
    label: "All Shops",
    name: "All Shops",
  });
  const dropdownShops = useApi(api.getDropdownShops);
  const [data, setData] = useState({});
  const [fromDate, setFromDate] = useState(moment().subtract(100, "years")._d);
  const [toDate, setToDate] = useState(moment().add(100, "years")._d);
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const obj = {
        shop: shop,
        date: { from: fromDate, to: toDate },
      };
      const res = await fetchOrdersStats.request(obj);
      if (res) setData(res?.data?.data);
    } catch (error) {} finally {
      setLoading(false);
    }
  };

  const fetchTrendData = async () => {
    try {
      const obj = {
        shop: shop,
        date: { from: fromDate, to: toDate },
      };
      const res = await fetchOrdersTrend.request(obj);
      if (res) {
        const trendItems = Array.isArray(res?.data?.data) ? res.data.data : [];
        setTrendData(trendItems);
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetchData();
    fetchTrendData();
  }, [shop, fromDate, toDate]);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

  const trendItems = Array.isArray(trendData) ? trendData : [];
  const labels = trendItems.map((i) => formatDate(i.date));

  const ordersData = trendItems.map((i) => i.orders);
  const amountData = trendItems.map((i) => i.amount);
  const cancelledOrders = trendItems.map((i) => i.cancelled);
  const deliveredOrders = trendItems.map((i) => i.completed);
  const dualLineData = {
    labels,
    datasets: [
      {
        label: "Orders Placed",
        data: ordersData,
        borderColor: "#4dabf7",
        borderWidth: 3,
        tension: 0.35,
        yAxisID: "y",
        pointRadius: 2, // dots size
        pointHoverRadius: 4, // hover size
      },
      {
        label: "Order Amount",
        data: amountData,
        borderColor: "#40c057",
        borderWidth: 3,
        tension: 0.35,
        yAxisID: "y1",
        pointRadius: 2,
        pointHoverRadius: 4,
      },
      {
        label: "Cancelled Orders",
        data: cancelledOrders,
        borderColor: "#f68d8d",
        borderWidth: 1,
        tension: 0.35,
        yAxisID: "y",
        pointRadius: 1,
        pointHoverRadius: 2,
      },
      {
        label: "Delivered Orders",
        data: deliveredOrders,
        borderColor: "#78ae85",
        borderWidth: 2,
        tension: 0.35,
        yAxisID: "y",
        pointRadius: 1,
        pointHoverRadius: 2,
      },
    ],
  };
  const formatNumber = (num) => {
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(0) + "K";
    return num;
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => {
            if (ctx.dataset.label === "Order Amount") {
              return `PKR ${formatNumber(ctx.raw)}`;
            }
            return `${ctx.raw} orders`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)", // 👈 more faded
        },
        title: {
          display: true,
          text: "Orders",
        },
      },
      y1: {
        beginAtZero: true,
        position: "right",
        grid: {
          drawOnChartArea: false,
          color: "rgba(0,0,0,0.05)", // 👈 faded
        },
        ticks: {
          callback: function (value) {
            return formatNumber(value);
          },
        },
        title: {
          display: true,
          text: "Amount (PKR)",
        },
      },
      x: {
        grid: {
          color: "rgba(0,0,0,0.04)",
          borderDash: [2, 2], // subtle dashed grid
        },
      },
    },
  };

  const generateInsight = () => {
    if (ordersData.length < 2) return "";

    const first = ordersData[0];
    const last = ordersData[ordersData.length - 1];
    if (last > first) {
      return "Orders show an overall upward trend during this period.";
    } else if (last < first) {
      return "Orders declined compared to the beginning of the period.";
    }
    return "Orders remained relatively stable during this period.";
  };

  const getShops = async (inputText) => {
    return new Promise((resolve, reject) => {
      dropdownShops
        .request(inputText)
        .then((res) => {
          const getData = res.data.map((cat) => ({
            value: cat,
            label: (
              <>
                <strong className="fs-12" name={cat.shop_name}>
                  {cat.id}-{cat.shop_name}
                </strong>
                <p className="fs-10 text-lowercase">
                  {cat.whatsapp} <br />
                  {cat.username}
                </p>
              </>
            ),
            name: cat.shop_name + " [" + cat.id + "]",
          }));
          getData.splice(0, 0, {
            value: null,
            label: "All Shops",
            name: "All Shops",
          });
          resolve(getData);
        })
        .catch((err) => reject("err" + err));
    });
  };

  // Prepare chart data
  const doughnutData = {
    labels: ["Pending", "Confirmed", "Delivered", "Cancelled"],
    datasets: [
      {
        data: [data?.pending, data?.confirmed, data?.delivered, data?.cancelled],
        backgroundColor: ["#f7c948", "#4dabf7", "#35633f", "#fa5252"],
        hoverOffset: 8,
      },
    ],
  };

  const barData = {
    labels: ["Total", "Active", "Pending", "Delivered", "Cancelled"],
    datasets: [
      {
        label: "Orders Count",
        data: [data?.total, data?.active, data?.pending, data?.delivered, data?.cancelled],
        backgroundColor: ["#9247e3", "#889f48", "#2077be", "#35633f", "#fa5252"],
      },
    ],
  };
  const insightText = generateInsight();

  const fetchAll = () => {
    fetchData();
    fetchTrendData();
  };

  const spinKeyframes = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 0.8s linear infinite; display: inline-block; }`;

  return (
    <Container fluid className="px-xl-5">
      <style>{spinKeyframes}</style>
      <Row className="py-2 align-items-center">
        <Col md="6">
          <h5 className="fw-bold">Platform Orders Insights</h5>
          <p className="text-muted mb-0">Order counts by status, trends, and total order value.</p>
        </Col>
        <Col md="6" className="text-md-end mt-3 mt-md-0">
          <Button
            variant="light"
            size="sm"
            className="me-2 border"
            onClick={fetchAll}
            disabled={loading}
          >
            <BiRefresh size={20} className={loading ? "spin" : ""} />
          </Button>
          <div style={{ display: "inline-flex", gap: "12px", alignItems: "center" }}>
            <DatePicker important={true} fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate} />
            <div style={{ minWidth: "200px" }}>
              <ReactSelect placeholder="Select shop" value={shop} searchable={true} options={getShops} handleChange={(e) => setShop({ value: e.value, label: e.name })} onInputChange={(newInput) => newInput} />
            </div>
          </div>
        </Col>
      </Row>

      {loading && (
        <div className="text-center mt-4">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && (
        <>
          {/* Primary KPIs */}
          <Row className="g-3 mt-1 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard title="Total Orders" count={formatNumber(data?.total)} icon={<BiCart />} color={ECHART_COLORS.primary} />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard title="Active Orders" count={formatNumber(data?.active)} icon={<BiCheckCircle />} color={ECHART_COLORS.success} />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard title="Pending Confirmation" count={formatNumber(data?.pending)} icon={<BiTime />} color={ECHART_COLORS.accent} />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard title="Confirmed Orders" count={formatNumber(data?.confirmed)} icon={<BiCheckCircle />} color={ECHART_COLORS.teal} />
            </Col>
          </Row>

          {/* Secondary KPIs */}
          <Row className="g-3 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard title="Picked" count={formatNumber(data?.picked)} icon={<BiPackage />} color={ECHART_COLORS.purple} />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard title="Shipped" count={formatNumber(data?.shipped)} icon={<BiPackage />} color={ECHART_COLORS.emerald} />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard title="Delivered" count={formatNumber(data?.delivered)} icon={<BiCheckCircle />} color={ECHART_COLORS.success} />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard title="Returned" count={formatNumber(data?.returned)} icon={<BiXCircle />} color={ECHART_COLORS.danger} />
            </Col>
          </Row>

          {/* Tertiary KPIs */}
          <Row className="g-3 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard title="Cancelled" count={formatNumber(data?.cancelled)} icon={<BiXCircle />} color={ECHART_COLORS.danger} />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard title="Total Worth of Orders" count={`Rs. ${formatNumber(data?.total_value)}`} icon={<BiDollar />} color={ECHART_COLORS.accent} />
            </Col>
          </Row>

          {/* Charts */}
          <Row className="mt-4">
            <Col xl="12" lg="12" md="12" className="mt-4">
              <Card className="border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Orders Trend Over Time</h6>
                <div style={{ height: "400px" }}>
                  <Line data={dualLineData} options={lineOptions} />
                </div>
                <div style={{ marginTop: "10px", fontSize: "14px", color: "#6c757d" }}>{insightText}</div>
              </Card>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col xl="5" lg="5" md="12">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Order Status Breakdown</h6>
                <Doughnut data={doughnutData} />
              </Card>
            </Col>

            <Col xl="7" lg="7" md="12">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Order Distribution Chart</h6>
                <Bar
                  data={barData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 500,
                        },
                        suggestedMax: data?.total > 5000 ? data.total : 2000,
                      },
                    },
                  }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default OrdersStats;
